import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, ClickAwayListener, Collapse, Drawer, List, ListItemButton, ListItemText, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Images } from '../../utils/Images';

const DRAWER_WIDTH = 280;
const DRAWER_COLLAPSED_WIDTH = 70;

const SidebarComponent = ({ sidebarOpen, toggleSidebar }) => {

    const location = useLocation();
    const navigate = useNavigate();

    const SidebarMENU = useSelector((s) => s.reduxMaster.sidebarMenu);
    const [openSubMenu, setOpenSubMenu] = useState(null);

    const isActive = (path) => location.pathname === path;

    const toggleSubmenu = (name) => {
        setOpenSubMenu((prev) => (prev === name ? null : name));
    };

    useEffect(() => {
        const activeParent = SidebarMENU.find((item) =>
            item.children?.some((child) => child.link === location.pathname)
        );
        setOpenSubMenu(activeParent ? activeParent.name : null);
    }, [location.pathname]);

    return (
        <Drawer
            variant="permanent"
            open={sidebarOpen}
            sx={{
                width: sidebarOpen
                    ? DRAWER_WIDTH
                    : DRAWER_COLLAPSED_WIDTH,

                flexShrink: 0,

                '& .MuiDrawer-paper': {
                    width: sidebarOpen
                        ? DRAWER_WIDTH
                        : DRAWER_COLLAPSED_WIDTH,

                    transition:
                        'all 0.3s cubic-bezier(0.4,0,0.2,1)',

                    overflowX: 'hidden',
                    background: `var(--button-background-primary-color)`,
                    borderRight: 'none',
                    color: '#fff',
                    // boxShadow: '4px 0 20px rgba(0,0,0,0.12)',
                    borderBottomRightRadius: "24px",
                    borderTopRightRadius: "24px"
                },
            }}
        >
            <ClickAwayListener onClickAway={() => { if (sidebarOpen) { } }}>
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {/* HEADER */}
                    <Box
                        sx={{
                            minHeight: 82,
                            px: 2,
                            borderBottom:
                                '1px solid rgba(255,255,255,0.12)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: sidebarOpen
                                ? 'space-between'
                                : 'center',
                        }}
                    >
                        {sidebarOpen && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                }}
                            >
                                <img
                                    src={Images.companylogoWhite}
                                    alt="logo"
                                    style={{
                                        width: '180px',
                                        objectFit: 'contain',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => navigate('/dashboard')}
                                />
                            </Box>
                        )}
                        <div>
                            {sidebarOpen
                                ? <img src={Images.drawerClose} onClick={toggleSidebar} style={{ cursor: "pointer" }} />
                                : <img src={Images.drawerOpen} onClick={toggleSidebar} style={{ cursor: "pointer", paddingTop: "10px" }} />}
                        </div>
                    </Box>

                    {/* MENU */}
                    <List
                        sx={{
                            flexGrow: 1,
                            px: 1.2,
                            py: 1.5,
                            overflowY: 'auto',

                            // Hide scrollbar but keep scrolling
                            scrollbarWidth: 'none',        // Firefox
                            msOverflowStyle: 'none',       // IE/Edge (legacy)
                            '&::-webkit-scrollbar': {      // Chrome, Edge (Chromium), Safari
                                display: 'none',
                            },
                        }}
                    >
                        {SidebarMENU.map((item) => {
                            const active = isActive(item.link);

                            const hasChildren = item.children && item.children.length > 0;

                            return (
                                <React.Fragment key={item.name}>
                                    <ListItemButton
                                        onClick={() =>
                                            hasChildren
                                                ? toggleSubmenu(item.name)
                                                : navigate(item.link)
                                        }
                                        sx={{
                                            minHeight: 46,
                                            borderRadius: '10px',
                                            px: 1.5,
                                            mb: 0.7,
                                            color: '#fff',
                                            background: active
                                                ? 'rgba(255,255,255,0.12)'
                                                : hasChildren && openSubMenu === item.name
                                                    ? 'rgba(255,255,255,0.10)'
                                                    : 'transparent',
                                            justifyContent: sidebarOpen
                                                ? 'initial'
                                                : 'center',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                background:
                                                    'rgba(255,255,255,0.10)',
                                            }
                                        }}
                                    >
                                        <Tooltip
                                            title={!sidebarOpen ? item.name : ''}
                                            placement="right"
                                            arrow
                                        >
                                            <Box
                                                component="img"
                                                src={item.icon}
                                                sx={{
                                                    width: 20,
                                                    height: 20,
                                                    mr: sidebarOpen ? 1.5 : 0,
                                                    filter:
                                                        'brightness(0) invert(1)',
                                                }}
                                            />
                                        </Tooltip>

                                        <ListItemText
                                            primary={sidebarOpen && item.name}
                                            style={{ flex: sidebarOpen ? '1 1 auto' : 'none' }}
                                            primaryTypographyProps={{
                                                fontSize: 18,
                                                fontWeight: 200,
                                                fontFamily: 'Finlandica',
                                                lineHeight: "100%",
                                            }}
                                            sx={{
                                                opacity: sidebarOpen ? 1 : 0,
                                            }}
                                        />

                                        {hasChildren && sidebarOpen && (
                                            openSubMenu === item.name
                                                ? <ExpandLess fontSize="small" />
                                                : <ExpandMore fontSize="small" />
                                        )}
                                    </ListItemButton>

                                    {/* SUB MENU */}
                                    {hasChildren && (
                                        <Collapse
                                            in={openSubMenu === item.name}
                                            timeout="auto"
                                            unmountOnExit
                                        >
                                            <Box
                                                sx={{
                                                    ml: sidebarOpen ? 2.4 : 0,
                                                    mt: 0.3,
                                                    position: 'relative',
                                                    '&::before': sidebarOpen ? {
                                                        content: '""',
                                                        position: 'absolute',
                                                        left: '8px',
                                                        top: '0px',
                                                        bottom: '26px',
                                                        width: '1px',
                                                        background:
                                                            'rgba(255,255,255,0.55)',
                                                    } : { display: "none" },
                                                }}
                                            >
                                                {item.children.map((child) => {
                                                    const childActive = isActive(child.link);
                                                    return <ListItemButton
                                                        onClick={() => navigate(child.link)}
                                                        key={child.name}
                                                        sx={{
                                                            minHeight: 28,
                                                            py: sidebarOpen ? 0 : 1,
                                                            pl: sidebarOpen ? 3 : 1.5,
                                                            position: 'relative',
                                                            color: '#fff',
                                                            '&:hover': {
                                                                background: 'transparent',
                                                            },

                                                            /* CURVE LINE */
                                                            '&::before': sidebarOpen ? {
                                                                content: '""',
                                                                position: 'absolute',
                                                                left: '8px',
                                                                top: '0px',
                                                                width: '12px',
                                                                height: '15px',
                                                                borderLeft:
                                                                    '1px solid rgba(255,255,255,0.55)',
                                                                borderBottom:
                                                                    '1px solid rgba(255,255,255,0.55)',
                                                                borderBottomLeftRadius: '8px',
                                                            } : { display: "none" },
                                                        }}
                                                    >
                                                        {/* SMALL DOT */}
                                                        {sidebarOpen && <Box
                                                            sx={{
                                                                width: 4,
                                                                height: 4,
                                                                borderRadius: '50%',
                                                                background: '#fff',
                                                                position: 'absolute',
                                                                left: '18px',
                                                                top: '46%',
                                                                transform:
                                                                    'translateY(-20%)',
                                                            }}
                                                        />}
                                                        {/* Sidebar menu icons */}
                                                        {!sidebarOpen ?
                                                            <Tooltip title={child.name} arrow placement="right">
                                                                <img src={child.icon} style={{ marginRight: sidebarOpen ? "10px" : "0px", transition: "0.2s" }} />
                                                            </Tooltip>
                                                            : <ListItemText primary={child.name}
                                                                primaryTypographyProps={{ fontSize: 16, marginLeft: "5px", fontWeight: childActive ? 'bold' : 400, noWrap: true, sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', }, fontFamily: "Finlandica" }} />}
                                                        {/* Sidebar sub menu text */}
                                                    </ListItemButton>
                                                })}
                                            </Box>
                                        </Collapse>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </List>
                </Box>
            </ClickAwayListener>
        </Drawer>
    );
};

export default SidebarComponent;